import {CheckerState, Plugins} from '@packages/systems/wfdl';
import {
  EHole as CEHole,
  annotate,
} from '@packages/utilities/contextual-expression';
import {
  TTypeApplication,
  TTypeConstructor,
  TUnknown,
} from '@packages/systems/wfdl-ast';
import {pluginName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {findAvailableBindings} from '@packages/systems/wfdl-findBindings';
import {filter, isNotEmpty} from '@packages/utilities/lazy-list';
import {getFirst} from '@packages/utilities/Pair';
import {singleMemo, weakMemo} from '@packages/utilities/memo';

function _hasAvailableOperands(
  checkerState: CheckerState,
  plugins: Plugins.PluginsType
): boolean {
  // Create a hole expression annotated with the operand type
  const operandTypedHole = annotate(CEHole(), {
    type: TTypeApplication({
      con: TTypeConstructor(
        qualify(pluginName('ConditionsOperand'), typeName('Operand'))
      ),
      // We use the Unknown type so that findAvailableBindings matches all
      // visible Operand<T> -typed values in scope regardless of T. Id -1 is
      // used so that this doesn't conflict with any other unknowns.
      arg: TUnknown({id: -1, rigids: {}}),
    }),
    env: checkerState.env,
    errors: [],
  });

  const availableBindings = findAvailableBindings(
    operandTypedHole,
    checkerState,
    plugins
  );

  return (
    isNotEmpty(availableBindings.plugins) ||
    // dataProperties results don't count as operand binding options
    isNotEmpty(
      filter(
        availableBindings.local,
        (entry) => getFirst(entry) !== 'dataProperties'
      )
    )
  );
}

/**
 * Returns true if there are operands available for conditional conditions in
 * the given binding context.
 *
 * @param plugins - plugins for the current binding context
 * @param checkerState - type checker state for the current binding context
 */
export const hasAvailableOperands = singleMemo((plugins: Plugins.PluginsType) =>
  weakMemo((checkerState: CheckerState): boolean =>
    _hasAvailableOperands(checkerState, plugins)
  )
);
